import { DateUtility } from '../utils/date-utility';
import { CalendarMode, ClassBuilder, SelectOption } from '../types';

function validateModeProperties(
    mode: CalendarMode,
    selectedDate?: string,
    selectedStartDate?: string,
    selectedEndDate?: string
): void {
    if (mode === 'single') {
        if ((selectedStartDate || selectedEndDate) && !selectedDate) {
            console.warn('Calendar: Single mode but range properties set. Use selectedDate instead.');
        }
    } else if (mode === 'range') {
        if (selectedDate && !selectedStartDate && !selectedEndDate) {
            console.warn(
                'Calendar: Range mode detected but single property is set. Use selectedStartDate/selectedEndDate instead.'
            );
        }
    }
}

/**
 * Resolves the localized month name for a given year and month.
 * Uses Intl.DateTimeFormat for localization with fallback to month number.
 * @param lang Language code (e.g., 'de-CH', 'fr-CH')
 * @param year Year (e.g., 2025)
 * @param month Month (1-12)
 * @returns Localized month name with first letter capitalized, or month number as fallback
 */
function resolveMonthTranslation(lang: string, year: number, month: number): string {
    try {
        const date = new Date(year, month - 1, 1);
        const monthName = date.toLocaleString(lang, { month: 'long' });
        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    } catch {
        return String(month);
    }
}

/**
 * Converts class builders to an array of CSS class strings.
 * Processes class builders and their nested modifiers to generate final class list.
 * @param classBuilder Array of class builder objects with conditional class application
 * @returns Array of CSS class strings
 */
function getClasses(classBuilder: ClassBuilder[]): string[] {
    const classes: string[] = [];

    classBuilder.forEach((modifier) => {
        if (modifier.case) {
            classes.push(...modifier.class);

            if (modifier.modifier) {
                modifier.modifier.forEach((subModifier) => {
                    if (subModifier.case) {
                        classes.push(...subModifier.class);
                    }
                });
            }
        }
    });

    return classes;
}

function getMonthKey(year: number, month: number): string {
    return JSON.stringify({ year, month });
}

/**
 * Generates month options for the calendar dropdown selector.
 * Creates options starting from the flexible start date or today, up to the specified limit.
 * @param flexibleStartDate Start date string in YYYY-MM-DD format (optional)
 * @param todayString Today's date string in YYYY-MM-DD format
 * @param monthDropdownLimit Maximum number of months to include in options
 * @param lang Language code (e.g., 'de-CH', 'fr-CH')
 * @returns Array of month options with label, value, and group
 */
function getMonthOptions(
    flexibleStartDate: string,
    todayString: string,
    monthDropdownLimit: number,
    lang: string
): SelectOption[] {
    try {
        const startDate = flexibleStartDate || todayString;
        const monthTravelStart = DateUtility.getMonthAsNumber(startDate);
        const yearTravelStart = DateUtility.getYearAsNumber(startDate);
        const monthOptions: SelectOption[] = [];
        let monthOption = monthTravelStart;
        let yearOption = yearTravelStart;

        for (let m = 0; m <= monthDropdownLimit; m++) {
            monthOptions.push({
                label: `${CalendarUtility.resolveMonthTranslation(lang, yearOption, monthOption)} ${yearOption}`,
                value: CalendarUtility.getMonthKey(yearOption, monthOption),
                group: yearOption.toString(),
            });
            monthOption = monthOption < 12 ? ++monthOption : 1;
            yearOption = monthOption === 1 ? ++yearOption : yearOption;
        }

        return monthOptions;
    } catch (error) {
        console.error('Calendar: Error generating month options:', error);
        return [];
    }
}

export const CalendarUtility = {
    validateModeProperties,
    resolveMonthTranslation,
    getClasses,
    getMonthKey,
    getMonthOptions,
};
